import { shallow } from 'zustand/shallow';
import { useEditorStore } from '../editor-store';
import { Block, Section } from '../types';
import { useClipboardStore } from './clipboard.store';
import { useCurrentOrganization } from 'src/store/organization';
import { trackSegmentEvent } from 'src/utils/segment';
import { useEditorContext } from 'src/store/editor';

export const useClipboardActions = () => {
  const journeyAliasId = useEditorContext((state) => state.journey?.aliases[0].id, shallow);
  const orgSlug = useCurrentOrganization((state) => state.currentOrganization?.slug || '');

  const getSectionById = useEditorStore((state) => state.getSectionById);
  const getBlockById = useEditorStore((state) => state.getBlockById);
  const dispatchUserEditorAction = useEditorStore((state) => state.dispatchUserEditorAction);

  const activeBlockActionsForBlockId = useClipboardStore((state) => state.blockActionsForBlockId, shallow);

  const copyBlock = useClipboardStore((state) => state.copyBlock);
  const cutBlock = useClipboardStore((state) => state.cutBlock);
  const copySection = useClipboardStore((state) => state.copySection);

  const copyBlockByBlockId = async (blockId: Block['id']) => {
    const blockToCopy = getBlockById(blockId);
    if (blockToCopy) {
      if (activeBlockActionsForBlockId.includes(blockId)) {
        return;
      }
      await copyBlock(blockToCopy);
      trackSegmentEvent('Block Copied', {
        slug: orgSlug,
        aliasId: journeyAliasId,
      });
    }
  };

  const copySectionBySectionId = (sectionId: Section['id']) => {
    const section = getSectionById(sectionId);
    if (!section) {
      return Promise.resolve();
    }
    return copySection(section);
  };

  const copyFolder = (sectionIds: string[]) => {
    const sections: Section[] = []
    
    sectionIds.forEach(id => {
      const section = getSectionById(id)
      
      if(section) {
        sections.push(section)
      }
    })

    if (!sections?.length) {
      return Promise.resolve();
    }

    return copySection(sections);
  };

  const cutBlockByBlockId = async (blockId: Block['id']) => {
    const blockToCut = getBlockById(blockId);
    if (blockToCut) {
      if (activeBlockActionsForBlockId.includes(blockId)) {
        return;
      }
      await cutBlock(blockToCut, () => {
        dispatchUserEditorAction({
          type: 'delete-block',
          id: blockToCut.id,
        });
        trackSegmentEvent('Block Cut', {
          slug: orgSlug,
          aliasId: journeyAliasId,
        });
      });
    }
  };

  return { copyBlockByBlockId, cutBlockByBlockId, copySectionBySectionId, copyFolder };
};
